import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';
import {
  notifyAskerAnswered,
  notifyAskerClosed,
  notifyOtherSuppliersPublished,
} from '../../lib/briefQuestionNotify';
import QACard from './QACard';

// Admin-side "Questions from suppliers" section (supplier-questions brief §6/§1
// + admin tidy-up tweak brief). Sits after Recipients & responses, above the
// internal notes and activity. Inline answer composer per question — no modal.
//
// Grouped by STATUS not supplier (tweak §3) — a brief owner is clearing a queue,
// not browsing by company. "Needs an answer" (oldest first, longest wait most
// urgent) above "Answered" (newest first, it's a record). Answered collapses
// beyond three; filters appear only past ~4 questions.
//
// The audience choice is the whole design (§1): default "Just this supplier"
// (publishing is one-way/permanent); an answered private question can be
// published later; published Q&A is anonymous to other suppliers.

// Advisory-only scope nudge (§1): if an answer reads like it changes what gets
// quoted, gently suggest sharing. Never forces anything.
const SCOPE_HINTS = /\b(yes|no|must|need|require|required|weight|load|size|dimension|instead|actually|change|cannot|can't|not able|minimum|maximum|at least|up to)\b/i;

const PURPLE = '#7C5CBF';
const FILTERS = [{ key: 'all', label: 'All' }, { key: 'unanswered', label: 'Unanswered' }, { key: 'shared', label: 'Shared' }];

export default function AdminBriefQuestions({ briefId, briefName, currentUserId, onChanged, onCountChange, supplierCount = 0 }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});   // questionId -> { body, audience }
  const [busy, setBusy] = useState(null);       // questionId currently saving
  const [notice, setNotice] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showAllAnswered, setShowAllAnswered] = useState(false);
  // Answered questions collapse to a one-line summary by default so a long
  // thread doesn't blow up the page; click to expand the full card.
  const [expandedIds, setExpandedIds] = useState(new Set());
  const toggleExpand = (id) => setExpandedIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const load = useCallback(async () => {
    if (!briefId) return;
    setLoading(true);
    const { data } = await supabase
      .from('brief_questions')
      .select('id, body, status, visibility, published_at, created_at, supplier_id, suppliers(supplier_name), asker:supplier_users!asked_by_user_id(full_name), brief_question_answers(id, body, created_at, published, answered_by_user_id, attachment_url, attachment_name, answerer:internal_users!answered_by_user_id(full_name))')
      .eq('brief_id', briefId)
      .order('created_at', { ascending: true });
    setQuestions(data || []);
    setLoading(false);
  }, [briefId]);

  useEffect(() => { load(); }, [load]);

  // Report open count + oldest-waiting age up (rail "Needs you", §6).
  useEffect(() => {
    if (!onCountChange) return;
    const open = questions.filter((q) => q.status === 'open');
    const oldest = open.reduce((min, q) => Math.min(min, new Date(q.created_at).getTime()), Infinity);
    const oldestDays = open.length ? Math.floor((Date.now() - oldest) / 86400000) : null;
    onCountChange(open.length, oldestDays);
  }, [questions, onCountChange]);

  const draftFor = (qid) => drafts[qid] || { body: '', audience: 'private' };
  const setDraft = (qid, patch) => setDrafts((d) => ({ ...d, [qid]: { ...draftFor(qid), ...patch } }));
  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(null), 8000); };

  const submitAnswer = async (q) => {
    const draft = draftFor(q.id);
    const body = draft.body.trim();
    if (!body) return;
    const publish = draft.audience === 'everyone';
    setBusy(q.id);
    const nowIso = new Date().toISOString();
    // Optional attachment — upload first so a failed upload doesn't leave a
    // half-answered question.
    let attachmentUrl = null, attachmentName = null;
    if (draft.file) {
      const safe = draft.file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `__brief-question-answers/${q.id}-${Date.now()}-${safe}`;
      const { error: upErr } = await supabase.storage.from('supplier-uploads').upload(path, draft.file, { contentType: draft.file.type, upsert: false });
      if (upErr) { setBusy(null); flash("Couldn't upload the attachment — please try again."); return; }
      const { data: pub } = supabase.storage.from('supplier-uploads').getPublicUrl(path);
      attachmentUrl = pub?.publicUrl || null;
      attachmentName = draft.file.name;
    }
    const { error: ansErr } = await supabase.from('brief_question_answers').insert([{
      question_id: q.id, answered_by_user_id: currentUserId || null, body, published: publish,
      attachment_url: attachmentUrl, attachment_name: attachmentName,
    }]);
    if (ansErr) { setBusy(null); flash("Couldn't save the answer — please try again."); return; }
    // The status flip was unchecked: the answer saved, this failed, and the
    // question stayed in "needs an answer" while the toast said it was sent.
    const { error: statusErr } = await supabase.from('brief_questions').update({
      status: 'answered',
      ...(publish ? { visibility: 'published', published_at: nowIso } : {}),
    }).eq('id', q.id);
    if (statusErr) {
      setBusy(null);
      flash(`Your answer saved, but the question didn't move out of the queue — ${statusErr.message}`);
      load();
      return;
    }
    const asker = await notifyAskerAnswered({ supplierId: q.supplier_id, briefName, sentBy: currentUserId }).catch(() => ({ noAddress: false }));
    let others = null;
    if (publish) others = await notifyOtherSuppliersPublished({ briefId, briefName, exceptSupplierId: q.supplier_id, sentBy: currentUserId }).catch(() => null);
    setBusy(null);
    setDrafts((d) => { const n = { ...d }; delete n[q.id]; return n; });
    let msg = publish ? 'Answer published to everyone quoting.' : 'Answer sent to the supplier.';
    if (asker?.noAddress) msg += ' Note: this supplier has no active portal user, so no email was sent.';
    if (others?.noAddressCount) msg += ` ${others.noAddressCount} other supplier(s) have no active portal user and weren't emailed.`;
    flash(msg);
    load();
    onChanged?.();
  };

  const publishLater = async (q) => {
    const answers = (q.brief_question_answers || []);
    if (answers.length === 0) return;
    setBusy(q.id);
    const nowIso = new Date().toISOString();
    const latest = answers.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    // Both writes were unchecked while the toast said "Shared with everyone
    // quoting" regardless — and the emails below went out pointing at an answer
    // that might still be unpublished. Check before claiming, and before
    // telling other suppliers to go and read it.
    const { error: visErr } = await supabase.from('brief_questions').update({ visibility: 'published', published_at: nowIso }).eq('id', q.id);
    const { error: ansPubErr } = visErr ? { error: null } : await supabase.from('brief_question_answers').update({ published: true }).eq('id', latest.id);
    if (visErr || ansPubErr) {
      setBusy(null);
      flash(`Couldn't share it — ${(visErr || ansPubErr).message}. Nobody has been emailed.`);
      load();
      return;
    }
    const others = await notifyOtherSuppliersPublished({ briefId, briefName, exceptSupplierId: q.supplier_id, sentBy: currentUserId }).catch(() => null);
    setBusy(null);
    let msg = 'Shared with everyone quoting.';
    if (others?.noAddressCount) msg += ` ${others.noAddressCount} supplier(s) have no active portal user and weren't emailed.`;
    flash(msg);
    load();
    onChanged?.();
  };

  const closeUnanswered = async (q) => {
    setBusy(q.id);
    // Don't tell the supplier it's closed until it is.
    const { error: closeErr } = await supabase.from('brief_questions').update({ status: 'closed_unanswered' }).eq('id', q.id);
    if (closeErr) {
      setBusy(null);
      flash(`Couldn't close it — ${closeErr.message}. The supplier hasn't been emailed.`);
      load();
      return;
    }
    const asker = await notifyAskerClosed({ supplierId: q.supplier_id, briefName, sentBy: currentUserId }).catch(() => ({ noAddress: false }));
    setBusy(null);
    flash(asker?.noAddress ? 'Closed — but this supplier has no active portal user, so no email was sent.' : 'Closed — the supplier has been told.');
    load();
    onChanged?.();
  };

  // Filter, then split into groups. Needs-answer oldest-first; answered/closed
  // newest-first (§3).
  const filtered = useMemo(() => {
    if (filter === 'unanswered') return questions.filter((q) => q.status === 'open');
    if (filter === 'shared') return questions.filter((q) => q.visibility === 'published');
    return questions;
  }, [questions, filter]);
  const needsAnswer = useMemo(() =>
    filtered.filter((q) => q.status === 'open').sort((a, b) => new Date(a.created_at) - new Date(b.created_at)),
  [filtered]);
  const answered = useMemo(() =>
    filtered.filter((q) => q.status !== 'open').sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
  [filtered]);
  const openCount = questions.filter((q) => q.status === 'open').length;
  const showFilters = questions.length > 4;

  const renderCard = (q, collapsible = false) => {
    const draft = draftFor(q.id);
    const scopeNudge = draft.audience === 'private' && SCOPE_HINTS.test(draft.body);
    const isBusy = busy === q.id;
    const isOpen = q.status === 'open';
    // Collapsed one-line summary for an answered/closed card (page-size control).
    if (collapsible && !expandedIds.has(q.id)) {
      return (
        <button
          key={q.id}
          id={`brief-question-${q.id}`}
          type="button"
          onClick={() => toggleExpand(q.id)}
          aria-expanded="false"
          className="w-full text-left border border-line rounded-lg px-4 py-2.5 bg-[#FBFCF8] hover:bg-[#F7F9F0] transition-colors flex items-center justify-between gap-3 scroll-mt-4">
          <p className="font-['Metropolis'] text-sm text-ink truncate min-w-0">
            <span className="font-semibold">{q.suppliers?.supplier_name || 'Supplier'}</span> — {q.body}
          </p>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`font-['Metropolis'] text-[9px] font-bold uppercase tracking-[0.15em] rounded-md px-2 py-0.5 border whitespace-nowrap ${q.visibility === 'published' ? 'text-fusion-forest border-[#92D400]/40 bg-[#92D400]/5' : 'text-[#51534A] border-[#DADCCD] bg-white'}`}>
              {q.visibility === 'published' ? 'Shared' : 'Private'}
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#51534A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
          </div>
        </button>
      );
    }
    // The composer occupies the answer slot when unanswered (§3) — QACard places
    // it against the ghost Fusion 2K tile, same position the answer will fill.
    const composer = isOpen ? (
      <div>
        <label htmlFor={`ans-${q.id}`} className="block font-['Metropolis'] text-[11px] font-semibold text-[#51534A] mb-1">
          Your answer — {draft.audience === 'everyone' ? 'everyone quoting will see this word for word' : 'the supplier sees this word for word'}
        </label>
        <textarea
          id={`ans-${q.id}`}
          value={draft.body}
          onChange={(e) => setDraft(q.id, { body: e.target.value })}
          rows={3}
          disabled={isBusy}
          placeholder={draft.audience === 'everyone' ? 'Shared with everyone quoting — the asker is never named.' : 'Only this supplier sees this.'}
          className="w-full bg-white border border-line rounded-lg text-ink px-3 py-2 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] disabled:opacity-60" />
        <fieldset className="mt-2">
          <legend className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.15em] text-[#51534A] mb-1">Who sees this answer?</legend>
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-2 cursor-pointer font-['Metropolis'] text-[13px] text-ink">
              <input type="radio" name={`aud-${q.id}`} checked={draft.audience !== 'everyone'} onChange={() => setDraft(q.id, { audience: 'private' })} className="accent-active-green" />
              Just this supplier
            </label>
            <label className="flex items-center gap-2 cursor-pointer font-['Metropolis'] text-[13px] text-ink">
              <input type="radio" name={`aud-${q.id}`} checked={draft.audience === 'everyone'} onChange={() => setDraft(q.id, { audience: 'everyone' })} className="accent-active-green" />
              Everyone quoting <span className="text-[#51534A]">(anonymous · permanent)</span>
            </label>
          </div>
        </fieldset>
        {scopeNudge && (
          <p className="font-['Metropolis'] text-[12px] text-[#7A4E10] bg-[#E89A2E]/10 border border-[#E89A2E]/30 rounded-md px-3 py-2 mt-2">
            This looks like it might change what gets quoted — consider sharing it with everyone so all quotes are priced on the same information.
          </p>
        )}
        {/* Optional attachment — e.g. a marked-up plan or spec sheet that
            answers the question better than words. Shared under the same
            audience rule as the answer. */}
        <div className="mt-2.5">
          {draft.file ? (
            <div className="inline-flex items-center gap-2 font-['Metropolis'] text-[13px] text-ink bg-[#F7F9F0] border border-line rounded-lg px-3 py-1.5">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
              <span className="truncate max-w-[240px]">{draft.file.name}</span>
              <button type="button" onClick={() => setDraft(q.id, { file: null })} disabled={isBusy}
                className="text-[#E0290A] hover:underline text-[11px] font-bold ml-1 disabled:opacity-50">Remove</button>
            </div>
          ) : (
            <label className="inline-flex items-center gap-2 cursor-pointer font-['Metropolis'] text-[12px] font-semibold text-fusion-mid-grey border border-line hover:border-[#DADCCD] hover:text-ink rounded-lg px-3 py-1.5 transition-colors">
              <input type="file" className="hidden" disabled={isBusy} accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) setDraft(q.id, { file: f }); e.target.value = ''; }} />
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
              Attach a file <span className="text-[#9BA08C] font-normal">(optional)</span>
            </label>
          )}
        </div>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <button type="button" onClick={() => submitAnswer(q)} disabled={isBusy || !draft.body.trim()}
            className="rounded-lg font-['Metropolis'] text-xs font-bold px-4 py-2 transition-colors bg-[#92D400] text-[#131313] hover:bg-[#a4e810] disabled:bg-[#E6EADB] disabled:text-[#9BA08C] disabled:cursor-not-allowed">
            {isBusy ? 'Sending…' : draft.audience === 'everyone' ? 'Send to everyone quoting' : 'Send to this supplier'}
          </button>
          <button type="button" onClick={() => closeUnanswered(q)} disabled={isBusy}
            className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-[#51534A] hover:text-[#E0290A] bg-transparent border border-line hover:border-[#E0290A]/50 rounded-full px-3 py-1.5 transition-colors disabled:opacity-50">
            Close without answering
          </button>
        </div>
      </div>
    ) : null;
    return (
      <div key={q.id} id={`brief-question-${q.id}`} className="scroll-mt-4">
        {collapsible && (
          <button type="button" onClick={() => toggleExpand(q.id)} aria-expanded="true"
            className="mb-1 font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-[#51534A] hover:text-fusion-forest inline-flex items-center gap-1 transition-colors">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="18 15 12 9 6 15" /></svg>
            Minimise
          </button>
        )}
        <QACard
          side="admin"
          supplierCount={supplierCount}
          composer={composer}
          question={{
            id: q.id, body: q.body, status: q.status, visibility: q.visibility, createdAt: q.created_at,
            askerName: q.asker?.full_name, askerOrg: q.suppliers?.supplier_name,
            answers: (q.brief_question_answers || []).map((a) => ({ id: a.id, body: a.body, createdAt: a.created_at, answererName: a.answerer?.full_name, attachmentUrl: a.attachment_url, attachmentName: a.attachment_name })),
          }}
        />
        {/* Publish-later — an answered PRIVATE question can be shared afterwards. */}
        {q.status === 'answered' && q.visibility !== 'published' && (
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <button type="button" onClick={() => publishLater(q)} disabled={isBusy}
              className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-fusion-forest bg-transparent border border-[#92D400]/50 hover:bg-[#92D400]/10 rounded-full px-3 py-1.5 transition-colors disabled:opacity-50">
              {isBusy ? 'Sharing…' : 'Share with everyone quoting'}
            </button>
            <p className="font-['Metropolis'] text-[11px] text-[#51534A]">Publishing is permanent and anonymous — the other suppliers are emailed.</p>
          </div>
        )}
      </div>
    );
  };

  const GroupHeader = ({ label, count, tone }) => (
    <div className="flex items-center gap-2 mb-2">
      <span className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-fusion-mid-grey">{label}</span>
      <span className={`font-['Metropolis'] text-[11px] font-semibold rounded-md px-2 py-0.5 ${tone}`}>{count}</span>
    </div>
  );

  return (
    <section aria-labelledby="admin-brief-questions-heading" className="mt-8">
      <div className="rounded-xl border border-line overflow-hidden" style={{ borderLeft: `4px solid ${PURPLE}` }}>
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-line bg-white flex-wrap">
          <div className="flex items-center gap-3">
            <h2 id="admin-brief-questions-heading" className="font-['Metropolis'] text-sm font-bold text-ink">Questions from suppliers</h2>
            <span className="font-['Metropolis'] text-[9px] font-bold uppercase tracking-[0.15em] rounded-md px-2 py-0.5" style={{ color: PURPLE, background: `${PURPLE}1A`, border: `1px solid ${PURPLE}55` }}>
              Suppliers see this
            </span>
          </div>
          <div className="flex items-center gap-3">
            {showFilters && (
              <div className="flex items-center gap-1">
                {FILTERS.map((f) => (
                  <button key={f.key} type="button" onClick={() => setFilter(f.key)}
                    className={`font-['Metropolis'] text-[11px] font-semibold rounded-full px-3 py-1 transition-colors ${filter === f.key ? 'bg-[#131313] text-white' : 'text-[#51534A] hover:bg-[#F1F3EA]'}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            )}
            {openCount > 0 && (
              <span className="font-['Metropolis'] text-[11px] font-semibold text-[#7A4E10] bg-[#E89A2E]/15 border border-[#E89A2E]/40 rounded-md px-2 py-0.5">
                {openCount} unanswered
              </span>
            )}
          </div>
        </div>

        <div className="p-5 bg-white">
          {notice && <p role="status" className="font-['Metropolis'] text-[13px] text-fusion-forest mb-3">{notice}</p>}
          {loading ? (
            <p className="font-['Metropolis'] text-sm text-[#51534A] italic">Loading questions…</p>
          ) : questions.length === 0 ? (
            <p className="font-['Metropolis'] text-sm text-[#51534A] italic">No questions yet. Suppliers can ask from the brief page until questions close.</p>
          ) : (needsAnswer.length === 0 && answered.length === 0) ? (
            <p className="font-['Metropolis'] text-sm text-[#51534A] italic">No questions match this filter.</p>
          ) : (
            <div className="space-y-6">
              {needsAnswer.length > 0 && (
                <div>
                  <GroupHeader label="Needs an answer" count={needsAnswer.length} tone="text-[#7A4E10] bg-[#E89A2E]/15" />
                  <div className="space-y-4">{needsAnswer.map(renderCard)}</div>
                </div>
              )}
              {answered.length > 0 && (
                <div>
                  <GroupHeader label="Answered" count={answered.length} tone="text-fusion-forest bg-[#92D400]/10" />
                  <div className="space-y-4">
                    {(showAllAnswered ? answered : answered.slice(0, 3)).map((q) => renderCard(q, true))}
                  </div>
                  {answered.length > 3 && (
                    <button type="button" onClick={() => setShowAllAnswered((v) => !v)}
                      className="mt-3 font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-fusion-forest hover:text-fusion-teal transition-colors">
                      {showAllAnswered ? 'Show fewer' : `Show ${answered.length - 3} more`}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
